/**
 * Device-level display preferences: theme, language and display currency.
 *
 * These are per-device conveniences, not account settings, so they live in
 * localStorage (guarded) and never block boot. Language changes go through
 * `setLanguage` in `@/i18n` so translations load before the UI switches.
 */
import { useSyncExternalStore } from 'react';
import { z } from 'zod';

import { readJson, writeJson } from './storage';

export const THEMES = ['light', 'dark'] as const;
export const LANGUAGE_CODES = ['en', 'sw', 'ar'] as const;
export const DISPLAY_CURRENCIES = ['KES', 'UGX', 'USD', 'EUR'] as const;

export type Theme = (typeof THEMES)[number];
export type LanguageCode = (typeof LANGUAGE_CODES)[number];
export type DisplayCurrency = (typeof DISPLAY_CURRENCIES)[number];

const preferencesSchema = z.object({
  theme: z.enum(THEMES),
  language: z.enum(LANGUAGE_CODES),
  currency: z.enum(DISPLAY_CURRENCIES),
});

export type Preferences = z.infer<typeof preferencesSchema>;

const STORAGE_KEY = 'kb.preferences';

function systemTheme(): Theme {
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

function browserLanguage(): LanguageCode {
  const preferred = typeof navigator === 'undefined' ? [] : (navigator.languages ?? []);
  for (const tag of preferred) {
    const base = tag.toLowerCase().split('-')[0];
    if (base === 'sw' || base === 'ar') return base;
    if (base === 'en') return 'en';
  }
  return 'en';
}

function load(): Preferences {
  const defaults: Preferences = {
    theme: systemTheme(),
    language: browserLanguage(),
    currency: 'KES',
  };
  const stored = preferencesSchema.partial().safeParse(readJson(STORAGE_KEY) ?? {});
  return stored.success ? { ...defaults, ...stored.data } : defaults;
}

let current: Preferences = load();
const listeners = new Set<() => void>();

export function getPreferences(): Preferences {
  return current;
}

export function updatePreferences(patch: Partial<Preferences>): void {
  current = { ...current, ...patch };
  writeJson(STORAGE_KEY, current);
  if (patch.theme) applyTheme(patch.theme);
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function usePreferences(): Preferences {
  return useSyncExternalStore(subscribe, getPreferences, getPreferences);
}

/** Reflect the theme on <html> (the stylesheet keys off `data-theme`). */
export function applyTheme(theme: Theme): void {
  const root = document.documentElement;
  root.dataset['theme'] = theme;
  root.style.colorScheme = theme;
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', theme === 'dark' ? '#121210' : '#f3f1ec');
}

/** Test helper: forget in-memory preferences and re-read storage. */
export function resetPreferencesForTests(): void {
  current = load();
}
